import { useCallback, useEffect, useRef, useState, type RefCallback, type RefObject } from 'react'

export function getScrollParent(element: HTMLElement | null): HTMLElement | null {
    if (!element) return null
    let parent = element.parentElement
    while (parent) {
        const { overflowY } = getComputedStyle(parent)
        if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
            return parent
        }
        parent = parent.parentElement
    }
    return null
}

export interface UseScrollSpyOptions {
    sectionIds: string[]
    rootRef?: RefObject<HTMLElement | null>
    /** Distance from top of scroll container to treat a section as active (sticky nav + offset). */
    activationOffset?: number
}

export function useScrollSpy({ sectionIds, rootRef, activationOffset = 120 }: UseScrollSpyOptions) {
    const [activeId, setActiveId] = useState(() => sectionIds[0] ?? '')
    const sectionElementsRef = useRef<Map<string, HTMLElement>>(new Map())
    const isClickScrollingRef = useRef(false)

    const setSectionRef = useCallback(
        (id: string): RefCallback<HTMLElement> =>
            (element) => {
                if (element) {
                    sectionElementsRef.current.set(id, element)
                } else {
                    sectionElementsRef.current.delete(id)
                }
            },
        []
    )

    const scrollToSection = useCallback((id: string) => {
        const element = sectionElementsRef.current.get(id) ?? document.getElementById(id)
        if (!element) return

        isClickScrollingRef.current = true
        setActiveId(id)
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })

        window.setTimeout(() => {
            isClickScrollingRef.current = false
        }, 800)
    }, [])

    const updateActiveFromScroll = useCallback(() => {
        if (isClickScrollingRef.current) return

        const firstId = sectionIds[0]
        if (!firstId) return

        const firstEl = sectionElementsRef.current.get(firstId) ?? document.getElementById(firstId)
        if (!firstEl) return

        const scrollRoot = rootRef?.current ?? getScrollParent(firstEl) ?? null
        let nextActive = sectionIds[0] ?? ''

        if (scrollRoot) {
            const rootRect = scrollRoot.getBoundingClientRect()
            const activationY = rootRect.top + activationOffset

            for (const id of sectionIds) {
                const element = sectionElementsRef.current.get(id) ?? document.getElementById(id)
                if (!element) continue

                if (element.getBoundingClientRect().top <= activationY) {
                    nextActive = id
                }
            }

            // Only pin to the last section when the user is actually near the bottom of
            // the scroll container. Checking "last section bottom is visible" alone is too
            // aggressive when the last section is short (it becomes visible while earlier
            // sections are still the main content on screen).
            const distanceFromBottom = scrollRoot.scrollHeight - scrollRoot.scrollTop - scrollRoot.clientHeight
            if (distanceFromBottom <= 24) {
                nextActive = sectionIds[sectionIds.length - 1] ?? nextActive
            }
        } else {
            for (const id of sectionIds) {
                const element = sectionElementsRef.current.get(id) ?? document.getElementById(id)
                if (!element) continue

                if (element.getBoundingClientRect().top <= activationOffset) {
                    nextActive = id
                }
            }
        }

        setActiveId((current) => (current === nextActive ? current : nextActive))
    }, [sectionIds, rootRef, activationOffset])

    useEffect(() => {
        if (sectionIds.length === 0) return
        if (!activeId || !sectionIds.includes(activeId)) {
            setActiveId(sectionIds[0])
        }
    }, [sectionIds, activeId])

    useEffect(() => {
        if (sectionIds.length === 0) return

        let scrollRoot: HTMLElement | null = null
        const rafId = requestAnimationFrame(() => {
            const firstId = sectionIds[0]
            const firstEl = firstId ? (sectionElementsRef.current.get(firstId) ?? document.getElementById(firstId)) : null

            if (!firstEl) return

            scrollRoot = rootRef?.current ?? getScrollParent(firstEl) ?? null
            updateActiveFromScroll()

            scrollRoot?.addEventListener('scroll', updateActiveFromScroll, { passive: true })
            window.addEventListener('scroll', updateActiveFromScroll, { passive: true })
            window.addEventListener('resize', updateActiveFromScroll)
        })

        return () => {
            cancelAnimationFrame(rafId)
            scrollRoot?.removeEventListener('scroll', updateActiveFromScroll)
            window.removeEventListener('scroll', updateActiveFromScroll)
            window.removeEventListener('resize', updateActiveFromScroll)
        }
    }, [sectionIds, rootRef, updateActiveFromScroll])

    return { activeId, scrollToSection, setSectionRef }
}
